/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import CloseIcon from "@material-ui/icons/Close";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import ConfirmationNumberOutlinedIcon from "@material-ui/icons/ConfirmationNumberOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import PlaceOutlinedIcon from "@material-ui/icons/PlaceOutlined";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import ticket_icon from "designs/images/ticket_icon.png";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { convertDatetimeToHHII, replaceBr } from "utility";
import { removeBr } from "utility/convertBrTo";

import {
  backdropStyle,
  cardStyle,
  detailIconStyle,
  detailRowStyle,
  detailTextStyle,
  fabIconStyle,
  fabLayerStyle,
  fabText1Style,
  fabText2Style,
  fabTextStyle,
  modalCloseButtonStyle,
  modalCtaStyle,
  modalHeaderIconStyle,
  modalHeaderRowStyle,
  modalHeaderStyle,
  modalHeaderTitleIndexStyle,
  modalHeaderTitleStyle,
  modalLayerStyle,
  nextButtonStyle,
  prevButtonStyle,
  ringStyle,
  thumbnailContainerStyle,
  thumbnailImgStyle,
  thumbnailWrapStyle,
} from "./TodayEventMorphCard.styles";
import { useCardInlineStyle } from "./useCardInlineStyle";
import { useEventPlace } from "./useEventPlace";
import { useMorphState } from "./useMorphState";

import type { Timestamp } from "@google-cloud/firestore";
import type { Event } from "@spwn/types/firebase/firestore";
import type { FC } from "react";

const formatMonthDayWithWeekday = (timestamp: Timestamp): string => {
  return format(new Date(timestamp.seconds * 1000), "MM/dd(E)", { locale: ja });
};

const ChevronIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    aria-hidden="true"
  >
    <rect width="36" height="36" rx="18" fill="black" fillOpacity="0.5" />
    <path
      d="M15 9L12.885 11.115L19.755 18L12.885 24.885L15 27L24 18L15 9Z"
      fill="white"
    />
  </svg>
);

export const TodayEventMorphCard: FC<{
  events: Event[];
}> = ({ events }) => {
  const { t } = useI18n();

  const { morphState, openModal, closeModal } = useMorphState();

  const total = events.length;
  const [displayedEventIndex, setDisplayedEventIndex] = useState(0);
  const safeIndex = Math.min(displayedEventIndex, total - 1);
  const currentEvent = events[safeIndex];

  const fetchedPlace = useEventPlace(currentEvent);

  const cardStyleInline = useCardInlineStyle(morphState, total > 1);

  if (!currentEvent) {
    return null;
  }

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDisplayedEventIndex((i) => (i - 1 + total) % total);
  };
  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDisplayedEventIndex((i) => (i + 1) % total);
  };

  const [part] = currentEvent.parts;
  const openTime = part ? convertDatetimeToHHII(part.openTime) : "";
  const openDate = part ? formatMonthDayWithWeekday(part.openTime) : "";

  const venueText = fetchedPlace
    ? `${fetchedPlace.areaName} / ${fetchedPlace.venueName}`
    : undefined;

  const href = `/account/ticket/${currentEvent._id ?? ""}`;
  const fabSubLabel =
    total > 1 ? t("ticket.todayEvent.title") : removeBr(currentEvent.title);

  return (
    <React.Fragment>
      <div
        css={backdropStyle}
        data-state={morphState}
        onClick={closeModal}
        aria-hidden="true"
      />
      <div
        css={cardStyle}
        data-state={morphState}
        style={cardStyleInline}
        onClick={morphState === "fab" ? openModal : undefined}
        onKeyDown={
          morphState === "fab"
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openModal();
                }
              }
            : undefined
        }
        role={morphState === "fab" ? "button" : undefined}
        tabIndex={morphState === "fab" ? 0 : undefined}
        aria-label={
          morphState === "fab" ? t("ticket.todayEvent.title") : undefined
        }
      >
        <span css={ringStyle} data-state={morphState} aria-hidden="true" />
        <div
          css={fabLayerStyle}
          data-state={morphState}
          aria-hidden={morphState === "modal"}
        >
          <div css={fabIconStyle}>
            <img src={ticket_icon} alt="" width="20" />
          </div>
          <div css={fabTextStyle}>
            <div css={fabText1Style}>
              {total > 1
                ? t("ticket.todayEvent.purchased")
                : t("ticket.todayEvent.title")}
            </div>
            <div css={fabText2Style}>{fabSubLabel}</div>
          </div>
        </div>
        <div
          css={modalLayerStyle}
          data-state={morphState}
          aria-hidden={morphState !== "modal"}
        >
          <div css={modalHeaderStyle}>
            <div>
              {/* MUI v4 の .MuiSvgIcon-root が width/height/font-size を当てており、
                emotion の css prop では詳細度で勝てないため inline style で上書きする */}
              <ConfirmationNumberIcon
                css={modalHeaderIconStyle}
                style={{ width: 40, height: 40, fontSize: 40 }}
              />
            </div>
            <button
              type="button"
              css={modalCloseButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                closeModal();
              }}
            >
              <CloseIcon style={{ width: 24, height: 24, fontSize: 24 }} />
            </button>
            <div css={modalHeaderRowStyle}>
              {total > 1 ? (
                <button
                  css={prevButtonStyle}
                  onClick={handlePrev}
                  aria-label={t("ticket.todayEvent.prevEvent")}
                >
                  <ChevronIcon />
                </button>
              ) : null}
              <div css={modalHeaderTitleStyle}>
                {t("ticket.todayEvent.title")}
                {total > 1 ? (
                  <span css={modalHeaderTitleIndexStyle}>
                    ({safeIndex + 1}/{total})
                  </span>
                ) : null}
              </div>
              {total > 1 ? (
                <button
                  css={nextButtonStyle}
                  onClick={handleNext}
                  aria-label={t("ticket.todayEvent.nextEvent")}
                >
                  <ChevronIcon />
                </button>
              ) : null}
            </div>
          </div>
          {currentEvent.defaultImg ? (
            <div css={thumbnailContainerStyle}>
              <div css={thumbnailWrapStyle}>
                <img
                  src={currentEvent.defaultImg}
                  alt=""
                  css={thumbnailImgStyle}
                />
              </div>
            </div>
          ) : null}
          <div css={detailRowStyle}>
            <ConfirmationNumberOutlinedIcon css={detailIconStyle} />
            <div css={detailTextStyle}>{replaceBr(currentEvent.title)}</div>
          </div>
          <div css={detailRowStyle}>
            <EventOutlinedIcon css={detailIconStyle} />
            <div css={detailTextStyle}>
              {openDate} OPEN {openTime}
            </div>
          </div>
          {venueText ? (
            <div css={detailRowStyle}>
              <PlaceOutlinedIcon css={detailIconStyle} />
              <div css={detailTextStyle}>{venueText}</div>
            </div>
          ) : null}
          <Link
            to={href}
            css={modalCtaStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {t("ticket.viewDigitalTicket")}
          </Link>
        </div>
      </div>
    </React.Fragment>
  );
};
